import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RecaptchaComponent } from 'src/app/shared/components/molecules/recaptcha/recaptcha.component';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { DialogModalComponent } from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import {
  CodeMismatchException,
  ExpiredCodeException,
} from 'src/app/shared/errors/cognito/cognito-errors';
import { passwordValidator } from 'src/app/shared/validators/registration-validators';
import { sameValueTwoFields } from 'src/app/utils/form-validator';
import { RoutesKeys } from 'src/app/utils/routes';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { SerialValidationException } from '../../shared/errors/user-management-service/user-management-service-errors';
import { environment } from '../../../environments/environment';

@Component({
  templateUrl: './create-new-password.page.html',
  styleUrls: ['./create-new-password.page.scss'],
})
/**
 * Instantiates change password template with create new password elements
 */
export class CreateNewPasswordPage implements OnInit, OnDestroy {
  recaptchaEnabled = environment.recaptcha.enabled;
  formGroup = new UntypedFormGroup({
    password: new UntypedFormControl('', [
      Validators.required,
      passwordValidator(),
    ]),
    confirmPassword: new UntypedFormControl('', [Validators.required]),
    code: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(6),
    ]),
    recaptcha: new UntypedFormControl(
      '',
      this.recaptchaEnabled ? [Validators.required] : []
    ),
  });
  errorMessage = '';
  private subsink = new Subscription();
  private dialogData!: any;
  resendLoading = false;
  @ViewChild('captchaRef') captchaRef?: RecaptchaComponent;
  RoutesKeys = RoutesKeys;
  labels = {
    codeSent: 'verification-instructions-2',
    codeNotSent: 'too-many-attempts',
    captchaNeeded: 'captcha-needed-message',
  };

  /**
   * @constructor
   */
  constructor(
    private authenticationService: AuthenticationService,
    private translationService: TranslationService,
    private readonly snackbarService: SnackBarService,
    public dialog: MatDialog,
    private router: Router
  ) {}

  /**
   * Sets confirm password validator
   */
  ngOnInit() {
    sameValueTwoFields(
      this.formGroup.get('password')!,
      this.formGroup.get('confirmPassword')!,
      [Validators.required]
    );
    this.dialogData = {
      title: 'password-successfully-created-title',
      content: 'password-successfully-created-content',
      successButtonText: 'ok',
    };
    this.translationService.translateObject(this.dialogData);
    this.translationService.translateObject(this.labels);

    this.subsink.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.errorMessage = '';
      })
    );
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Create a new password
   */
  async resetPassword() {
    const { password, code, recaptcha } = this.formGroup.getRawValue();
    const recaptchaToken = this.recaptchaEnabled ? recaptcha : 'dev-bypass';
    try {
      await this.authenticationService.confirmResetPassword(
        password,
        code,
        recaptchaToken
      );
      this.openDialog();
    } catch (error) {
      if (
        error instanceof CodeMismatchException ||
        error instanceof ExpiredCodeException
      ) {
        this.errorMessage = 'confirm-passwd-reset-code-mismatch';
      } else {
        this.errorMessage = 'backend-comm-error';
      }
    } finally {
      this.translationService
        .translateString(this.errorMessage)
        .then((translated) => {
          this.errorMessage = translated;
        });
      this.captchaRef?.reset();
    }
  }

  /**
   * Decides if reset password button should be disabled
   * @return {boolean}
   */
  isResetPasswordDisabled(): boolean {
    return this.formGroup.invalid;
  }

  /**
   *  Open confirmation dialog
   */
  private openDialog() {
    const dialogRef = this.dialog.open(DialogModalComponent, {
      data: this.dialogData,
      autoFocus: false,
    });

    this.subsink.add(
      dialogRef.afterClosed().subscribe(() => {
        this.router.navigate([RoutesKeys.LOGIN]);
      })
    );
  }

  /**
   * Tells service to resend email
   */
  resendEmail() {
    this.resendLoading = true;
    const email = this.authenticationService.passwordResetEmail!;
    const { recaptcha } = this.formGroup.getRawValue();
    const recaptchaToken = this.recaptchaEnabled ? recaptcha : 'dev-bypass';
    this.authenticationService.resetPassword(email, recaptchaToken).subscribe({
      next: () => {
        this.snackbarService.success(this.labels.codeSent + email);
      },
      error: async (error) => {
        this.handleInviteError(error as Error);
        this.resendLoading = false;
      },
      complete: () => {
        this.resendLoading = false;
      },
    });
  }

  /**
   * @param {string} controlName
   * @return {FormControl}
   */
  getControl(controlName: string): FormControl {
    return this.formGroup.get(controlName) as FormControl;
  }

  /**
   * Navigate to FAQ page and set a timeout to go to the contactUs section
   */
  navigateToFAQPage() {
    this.router.navigate(['/' + RoutesKeys.FAQ]).then(() => {
      setTimeout(() => {
        this.scrollToSection('contactUs');
      }, 500);
    });
  }

  /**
   *
   * @param {string} sectionId
   */
  private scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * Handles possible errors in inviting a representative
   * @param {Error} error
   */
  private handleInviteError(error: Error) {
    let errorMsg = this.labels.codeNotSent;
    if (error instanceof SerialValidationException) {
      errorMsg = this.labels.captchaNeeded;
    }
    this.snackbarService.error(errorMsg);
  }
}
